import { ContractInterface, ethers } from 'ethers';
import { Abi, Address, ContractFunctionArgs, ContractFunctionName } from 'viem';
import { Chain } from 'viem/chains';
import * as allChains from 'viem/chains';

import { DEFAULT_TARGET_CHAIN } from '@shared/constants';

interface EstimatePriceParams<
  TAbi extends Abi | readonly unknown[],
  TFunctionName extends ContractFunctionName<TAbi, 'nonpayable' | 'payable'>,
> {
  abi: TAbi;
  chain: Chain;
  contractAddress: Address;
  functName: TFunctionName;
  walletAddress: Address;
  args?: ContractFunctionArgs<TAbi, 'nonpayable' | 'payable', TFunctionName> | [];
}

interface EstimatePriceResult {
  gasEstimate: bigint;
  gasPrice?: bigint;
  estimateGasWithExtra: bigint;
}

export function getChainById(chainId: number): Chain {
  const chains = Object.values(allChains) as Chain[];
  const selectedChain = chains.find((chain) => chain.id === chainId);
  return selectedChain || DEFAULT_TARGET_CHAIN;
}

// now add 30% to gas
const extraPrecentage = BigInt(130);

const defaultGasEstimate = BigInt(500_000);

export const getEstimateGasPrice = async <
  TAbi extends Abi | readonly unknown[],
  TFunctionName extends ContractFunctionName<TAbi, 'nonpayable' | 'payable'>,
>({
  abi,
  chain,
  contractAddress,
  functName,
  walletAddress,
  args = [],
}: EstimatePriceParams<TAbi, TFunctionName>): Promise<EstimatePriceResult> => {
  const rpcUrl = chain.rpcUrls.default.http[0];

  const provider = new ethers.providers.JsonRpcProvider(rpcUrl).getSigner(walletAddress);
  const contract = new ethers.Contract(contractAddress, abi as ContractInterface, provider);

  try {
    if (!Array.isArray(args)) {
      throw new Error('Args should be array type');
    }

    const gasEstimateBigNumber = await contract.estimateGas[functName](...args);
    const gasPriceBigNumber = await provider.getGasPrice();
    const gasEstimate = gasEstimateBigNumber.toBigInt();
    const estimateGasWithExtra = (gasEstimate * extraPrecentage) / BigInt(100);
    const gasPrice = gasPriceBigNumber.toBigInt();
    return { gasEstimate, gasPrice, estimateGasWithExtra };
  } catch (error: any) {
    if (error.message) {
      console.error('Message error:', error.message);
    }

    if (error.transaction) {
      console.error('transaction error:', error.transaction);
    }

    if (error.error) {
      console.error('Contract error:', error.error.message);
    }

    return { gasEstimate: defaultGasEstimate, estimateGasWithExtra: defaultGasEstimate };
  }
};
